// Quién puede ver y abrir lo del módulo de Contratos (P15, S10).
//
// Refleja la matriz de S10 (eClock6, SuscripcionesUsuariosController.Puede) para esconder lo que
// el API rechazaría. La API sigue siendo la barrera: esto sólo evita ofrecer lo que de todos
// modos fallaría, y sobre todo evita los 401 que en eClock Go cierran la sesión (base.js).
//
//   Contratos.Listar:      Administrador, Editor, Supervisor, Visor (S10 R1, D07.22).
//                          Colaborador, Interno y Desconocido no tienen caso: false.
//   Contratos.Ver:         los mismos cuatro (S10 R1).
//   Contratos.Edicion, Borrar: Administrador y Editor («Actualizar estado» y «Cancelar», S08 R31, R32).
//   Colaboradores.Ver:     Administrador y Editor (bloque Editor, case "Colaboradores").
//                          Supervisor y Visor no lo tienen: GET Colaboradores/{id} les responde
//                          401, así que no deben abrir el perfil ni el Cardex (S02 R13, E75, E86).
//   ProveedoresFirma:      sólo Administrador (S04 R39). También el catálogo de plantillas (S06 R50).
//                          Las rutas lo piden con meta.requierePermiso (PermisoFaltante, main.js).
//
// Para «abrir el perfil» de un colaborador, importar PuedeVerColaborador (P15-I19, S07 y P15-I63
// también): es la regla de Colaboradores.Ver. PuedeAbrirCardex es la misma regla con el nombre del
// enlace del círculo del indicador, que lleva al Cardex en «Contratos de trabajo».
//
// El superadministrador sin membresía entra como Administrador (getPerfilID lo resuelve así, igual
// que el backend con su SuscripcionUsuario sintético).

const EsUno = (SuscripcionesUsuarios, ...Funciones) => {
  if (SuscripcionesUsuarios == null) return false;
  return Funciones.some(
    (F) => typeof SuscripcionesUsuarios[F] === "function" && SuscripcionesUsuarios[F]() === true
  );
};

// Contratos.Listar: ver el indicador, el filtro «Documentos» y la tarjeta del perfil.
export function PuedeListarContratos(SuscripcionesUsuarios) {
  return EsUno(
    SuscripcionesUsuarios,
    "esAdministrador",
    "esEditor",
    "esSupervisor",
    "esVisor"
  );
}

// Contratos.Ver: abrir un archivo de contrato (contratoArchivo), «Descargar contratos» (contratosDescarga) y el
// historial de firma (contratoFirmaEventos) (S07 R32, S08 R33). La misma lista que Listar (S10 R1).
export function PuedeVerContratos(SuscripcionesUsuarios) {
  return PuedeListarContratos(SuscripcionesUsuarios);
}

// Contratos.Edicion: «Actualizar estado» de un contrato (firmaReconciliar, S08 R31). Contratos.Borrar:
// «Cancelar» (contratoCancelar, S08 R32). Administrador y Editor; Supervisor y Visor sólo tienen Listar y Ver.
export function PuedeEditarContratos(SuscripcionesUsuarios) {
  return EsUno(SuscripcionesUsuarios, "esAdministrador", "esEditor");
}

export function PuedeBorrarContratos(SuscripcionesUsuarios) {
  return EsUno(SuscripcionesUsuarios, "esAdministrador", "esEditor");
}

// Contratos.Nuevo: registrar un contrato a mano (contratoCrear), que ofrece la casilla «Es un contrato
// firmado» de la ventana de subida (S07 R35, E49). Administrador y Editor (S10 R1; bloque Editor,
// case "Contratos"); Supervisor y Visor sólo tienen Listar y Ver.
export function PuedeCrearContratos(SuscripcionesUsuarios) {
  return EsUno(SuscripcionesUsuarios, "esAdministrador", "esEditor");
}

// Colaboradores.Ver: abrir el perfil de un colaborador, y con él el Cardex en «Contratos de
// trabajo» desde el círculo del indicador.
export function PuedeAbrirCardex(SuscripcionesUsuarios) {
  return EsUno(SuscripcionesUsuarios, "esAdministrador", "esEditor");
}

// Colaboradores.Ver con su nombre: abrir el perfil (GET Colaboradores/{id}). Es la guarda de Abrir en
// Personal (D11.31, E86) y la que deben usar las demás pantallas que abren el perfil.
export const PuedeVerColaborador = PuedeAbrirCardex;

// La ruta de S07 R20 al Cardex, directo en Documentos › «Contratos de trabajo» (el perfil la lee
// desde P15-I108: Generales.comp.vue elige la pestaña y el explorador abre la carpeta).
export function RutaCardexContratos(ColaboradorID) {
  return `/personal/${ColaboradorID}?pestana=documentos&carpeta=contratos-de-trabajo`;
}

// Documentos.Ver y Documentos.Nuevo de las dos acciones del expediente (S07 R40): el ZIP
// (documentosDescarga) con Ver y la copia (documentosCopiar) con Nuevo. Sólo Administrador y Editor; sin
// ellos el API responde 401, que cierra la sesión, así que el explorador no los ofrece.
export function PuedeDescargarExpediente(SuscripcionesUsuarios) {
  return EsUno(SuscripcionesUsuarios, "esAdministrador", "esEditor");
}

export const PuedeCopiarExpediente = PuedeDescargarExpediente;

// ProveedoresFirma (Avanzado): sólo el Administrador de la suscripción y el superadministrador. La matriz
// de S10 responde false explícito para Editor, Supervisor y Visor (S04 R39, S10 R9).
export function PuedeConfigurarProveedor(SuscripcionesUsuarios) {
  return EsUno(SuscripcionesUsuarios, "esAdministrador");
}

// El catálogo de plantillas de Contratos: sólo Administradores (S06 R45, R50, E91).
export const PuedeConfigurarPlantillas = PuedeConfigurarProveedor;

// ContratosLotes.Listar y Ver: el listado de lotes y la vista de uno (S06 R36, R52; S10 R1:
// Administrador todo, Editor Listar/Ver/Nuevo/Edicion; Supervisor, Visor y Colaborador nada, y sin el
// permiso lotesListar responde 401, E93).
export function PuedeListarLotes(SuscripcionesUsuarios) {
  return EsUno(SuscripcionesUsuarios, "esAdministrador", "esEditor");
}

// ContratosLotes.Edicion y Contratos.Edicion: «Revisar y enviar a firma», «Reintentar sólo los
// pendientes» y «Actualizar estado» del lote (S06 R35, R36, E101). Sin ellos, el API responde 401.
export function PuedeProcesarLotes(SuscripcionesUsuarios) {
  return EsUno(SuscripcionesUsuarios, "esAdministrador", "esEditor");
}

// Las reglas que una ruta pide con `meta.requierePermiso` (S04 D11.6; la guarda de main.js). Un nombre
// que no está aquí no pasa: la guarda falla cerrada.
export const PERMISOS_RUTA = {
  ProveedoresFirma: PuedeConfigurarProveedor,
  Plantillas: PuedeConfigurarPlantillas,
  ContratosLotes: PuedeListarLotes,
};

/**
 * El primer permiso de `meta.requierePermiso` que la ruta `to` pide y el usuario no tiene, o `null`
 * si puede entrar. Lo evalúa el único `router.beforeEach` (main.js) antes de montar la pantalla: sin el
 * permiso, la ruta lleva a «Sin permiso» y la pantalla no llama a nada (S04 R40, E60).
 *
 * Se decide con el perfil ya guardado; sin perfil resuelto no pasa nadie (el perfil «Desconocido» no es
 * Administrador). Es comodidad, no seguridad: el API vuelve a decidir en cada llamada.
 */
export function PermisoFaltante(to, SuscripcionesUsuarios) {
  const Pedidos = ((to && to.matched) || [])
    .map((r) => r && r.meta && r.meta.requierePermiso)
    .filter((p) => p != null && p !== "");
  for (const Permiso of Pedidos) {
    const Regla = PERMISOS_RUTA[Permiso];
    let Pasa = false;
    try {
      Pasa = typeof Regla === "function" && Regla(SuscripcionesUsuarios) === true;
    } catch (e) {
      Pasa = false;
    }
    if (!Pasa) return Permiso;
  }
  return null;
}

export default {
  PuedeListarContratos,
  PuedeVerContratos,
  PuedeEditarContratos,
  PuedeBorrarContratos,
  PuedeCrearContratos,
  PuedeAbrirCardex,
  PuedeVerColaborador,
  RutaCardexContratos,
  PuedeDescargarExpediente,
  PuedeCopiarExpediente,
  PuedeConfigurarProveedor,
  PuedeConfigurarPlantillas,
  PuedeListarLotes,
  PuedeProcesarLotes,
  PERMISOS_RUTA,
  PermisoFaltante,
};
